import { coursesApi } from "./api/course.api";
import { CourseCatalogData, CourseOption, CourseSummary } from "../types/course.response";

/**
 * 课程展示数据(catalog)的会话级缓存,keyed by 后端 courses 字典的 key。
 * catalog 数据与用户状态正交:改课不需要失效,只有 structureVersion 变化
 * (数据管线更新)时整体清空。Home / Program / overlay 共享同一份缓存。
 */
const cache = new Map<string, CourseCatalogData>();
const inflight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
let version = 0;

function notify() {
    version++;
    listeners.forEach(listener => listener());
}

export function subscribeCatalog(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** 供 useSyncExternalStore 使用:缓存内容变化时版本号递增 */
export function getCatalogVersion(): number {
    return version;
}

export function getCatalogEntry(key: string): CourseCatalogData | undefined {
    return cache.get(key);
}

/**
 * 全量响应(重接口 / requirement-details)自带完整课程数据,直接灌入缓存。
 * 始终覆盖:全量响应是最新数据,顺带刷掉管线更新后可能过期的旧条目。
 */
export function seedCatalog(entries: Record<string, CourseOption | CourseCatalogData>): void {
    const keys = Object.keys(entries);
    if (keys.length === 0) return;
    for (const [key, entry] of Object.entries(entries)) {
        cache.set(key, entry);
    }
    notify();
}

/** structureVersion 变化(管线更新)时调用,粗粒度整体清空 */
export function clearCatalog(): void {
    if (cache.size === 0) return;
    cache.clear();
    notify();
}

/**
 * 确保 keys 对应的课程已补水。已缓存/在途的 key 自动去重;
 * 失败的 key 从在途表移除,下次调用会重试。
 */
export async function ensureHydrated(keys: string[]): Promise<void> {
    const missing = Array.from(new Set(keys)).filter(
        key => !cache.has(key) && !inflight.has(key)
    );

    if (missing.length > 0) {
        const request = coursesApi.getBatch(missing)
            .then(courses => {
                for (const [key, course] of Object.entries(courses)) {
                    cache.set(key, course);
                }
                notify();
            })
            .finally(() => {
                missing.forEach(key => inflight.delete(key));
            });
        missing.forEach(key => inflight.set(key, request));
    }

    const pending = Array.from(new Set(keys))
        .map(key => inflight.get(key))
        .filter((p): p is Promise<void> => p !== undefined);
    await Promise.all(pending);
}

/** 摘要(用户态)+ catalog(展示数据)合成完整 CourseOption */
export function composeCourseOption(
    summary: CourseSummary,
    catalog: CourseCatalogData,
): CourseOption {
    return {
        ...catalog,
        userItemId: summary.userItemId,
        userState: summary.userState,
    };
}
